import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Briefcase, Loader2, Search, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import PortfolioTable from '../../components/portfolio/PortfolioTable';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter,
} from '../../components/ui/dialog';
import usePortfolio from '../../hooks/usePortfolio';
import { useAuth } from '../../context/AuthContext';

const PortfolioList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('portfolio.create');
  const canUpdate = hasPermission('portfolio.update');
  const canDelete = hasPermission('portfolio.delete');
  const canReorder = hasPermission('portfolio.reorder');
  const { portfolios, loading, deletePortfolio: apiDeletePortfolio, reorderPortfolios, search, setSearch } = usePortfolio();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEdit = (item) => {
    navigate(`/portfolio/edit/${item.id}`);
  };

  const handleDelete = async () => {
    if (itemToDelete === null || itemToDelete === undefined) return;

    try {
      setIsDeleting(true);
      const success = await apiDeletePortfolio(itemToDelete);
      if (success) {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <span className="p-2 bg-primary/10 text-primary rounded-2xl">
              <Briefcase size={28} />
            </span>
            Portfolio Management
          </h1>
          <p className="text-slate-500 mt-1 font-medium">Showcase your projects and link them with business objectives.</p>
        </div>
        {canCreate && <Button
          onClick={() => navigate('/portfolio/add')}
          className="bg-primary hover:bg-primary/90 text-secondary rounded-2xl px-6 py-6 shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5"
        >
          <Plus size={20} className="mr-2" />
          Add Project
        </Button>}
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by name or tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-2xl border-slate-200 focus-visible:ring-primary"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-sm rounded-3xl border border-dashed border-slate-300">
          <Loader2 className="animate-spin text-primary mb-4" size={40} />
          <p className="text-slate-500 font-medium">Loading your portfolio...</p>
        </div>
      ) : portfolios.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-3xl border-2 border-dashed border-slate-100 shadow-sm">
          <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-400">
            <Briefcase size={40} />
          </div>
          <h3 className="text-xl font-bold text-slate-800">Your portfolio is empty</h3>
          <p className="text-slate-500 mb-8 max-w-xs mx-auto">Start by adding your first project to showcase it to the world.</p>
          {canCreate && <Button
            onClick={() => navigate('/portfolio/add')}
            className="bg-primary hover:bg-primary/90 text-secondary rounded-xl"
          >
            Create Your First Project
          </Button>}
        </div>
      ) : (
        <PortfolioTable 
          data={portfolios} 
          onEdit={handleEdit} 
          onDelete={(id) => {
            setItemToDelete(id);
            setDeleteDialogOpen(true);
          }} 
          onReorder={reorderPortfolios}
          canEdit={canUpdate}
          canDelete={canDelete}
          canReorder={canReorder}
        />
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="text-red-500" size={24} />
            </div>
            <DialogTitle className="text-center text-xl font-bold text-slate-900">Confirm Deletion</DialogTitle>
            <DialogDescription className="text-center text-slate-500 mt-2">
              Are you sure you want to delete this portfolio project? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="flex-1 rounded-2xl border-slate-200"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex-1 rounded-2xl bg-red-500 hover:bg-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Now'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioList;
